import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select date",
  className = "",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      const [year, month] = value.split('-');
      if (year && month) {
        setSelectedYear(parseInt(year));
        setSelectedMonth(parseInt(month) - 1); // Convert to 0-based index
        setCurrentYear(parseInt(year));
        setCurrentMonth(parseInt(month) - 1);
      }
    }
  }, [value]);

  // Close dropdown when clicking outside and calculate position
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const calculatePosition = () => {
      if (containerRef.current && isOpen) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 400; // Approximate height of dropdown
        
        if (rect.bottom + dropdownHeight > viewportHeight) {
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', calculatePosition);
    
    if (isOpen) {
      calculatePosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isOpen]);

  const handleYearChange = (year) => {
    setCurrentYear(year);
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setSelectedYear(currentYear);
    
    const monthStr = (monthIndex + 1).toString().padStart(2, '0');
    const yearStr = currentYear.toString();
    const dateValue = `${yearStr}-${monthStr}`;
    
    onChange(dateValue);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedYear(null);
    setSelectedMonth(null);
    onChange('');
  };

  const formatDisplayValue = () => {
    if (selectedYear && selectedMonth !== null) {
      return `${months[selectedMonth]} ${selectedYear}`;
    }
    return '';
  };

  const generateYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear + 5; year >= currentYear - 50; year--) {
      years.push(year);
    }
    return years;
  };

  const years = generateYearRange();

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div
        className={`flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
        } ${isOpen ? 'border-primary-500 ring-1 ring-primary-500' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <FiCalendar className="h-4 w-4 text-gray-400 mr-2" />
          <span className={formatDisplayValue() ? 'text-gray-900' : 'text-gray-500'}>
            {formatDisplayValue() || placeholder}
          </span>
        </div>
        <div className="flex items-center">
          {formatDisplayValue() && (
            <button
              onClick={handleClear}
              className="mr-1 p-1 hover:bg-gray-100 rounded"
            >
              <FiX className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <FiChevronLeft className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`absolute left-0 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto ${
            dropdownPosition === 'top' 
              ? 'bottom-full mb-1' 
              : 'top-full mt-1'
          }`}
        >
          <div className="p-3">
            {/* Year Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => handleYearChange(currentYear - 1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <FiChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-medium text-gray-900">{currentYear}</span>
              <button
                onClick={() => handleYearChange(currentYear + 1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Year Quick Select */}
            <div className="mb-3">
              <div className="text-xs font-medium text-gray-700 mb-1">Quick Year:</div>
              <div className="grid grid-cols-3 gap-1">
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`px-2 py-1 text-xs rounded ${
                      year === currentYear 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>

            {/* Months Grid */}
            <div className="grid grid-cols-3 gap-1">
              {months.map((month, index) => (
                <button
                  key={index}
                  onClick={() => handleMonthSelect(index)}
                  className={`px-2 py-1.5 text-xs rounded-md transition-colors ${
                    selectedYear === currentYear && selectedMonth === index
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>

            {/* Special Options */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-1">Special:</div>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    onChange('Present');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 rounded-md text-gray-700"
                >
                  Present
                </button>
                <button
                  onClick={() => {
                    onChange('Ongoing');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 rounded-md text-gray-700"
                >
                  Ongoing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
